import { useQuery } from '@tanstack/react-query'
import { useGlobalErrorCode } from '@opallabs/hooks'
import { buildMeQuery } from '../useAuthenticatedUser'

const VERSION_CHECK_INTERVAL = 30_000
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'

/**
 * Intermittently check if the Client bundle's version differs from the Server.
 * If the two versions drift in production, this means that a deployment has
 * happened and the user needs to refresh to get the latest frontned code bundles.
 *
 * This check can also be used to check for users with expired sessions. In this
 * case the /me endpoint will respond with a 401 and the user needs to be
 * redirected to the login screen.
 *
 * The check is skipped while the tab is hidden (`refetchIntervalInBackground`
 * is off), because most open tabs are background tabs.
 *
 * This would preferably be a dedicated endpoint we could use instead of relying
 * on the /me endpoint, but for the sake of time that endpoint hasn't been
 * implemented yet - @dentonalford
 */
export const useOpalVersionCheck = ({ enabled }: { enabled: boolean }) => {
  // After the expiry is known, each check only gets another 401
  const isSessionExpired = useGlobalErrorCode() === 'user_session_expired'

  useQuery({
    ...buildMeQuery(),
    enabled: !IS_DEVELOPMENT && enabled && !isSessionExpired,
    retry: false,
    refetchInterval: VERSION_CHECK_INTERVAL,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}
